import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Wrench, Plus, Trash, PencilSimple, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";

const DAYS = [
  { v: 0, l: "Lunedì" }, { v: 1, l: "Martedì" }, { v: 2, l: "Mercoledì" },
  { v: 3, l: "Giovedì" }, { v: 4, l: "Venerdì" }, { v: 5, l: "Sabato" }, { v: 6, l: "Domenica" },
];

function MaintenanceForm({ initial, onSubmit, onCancel }) {
  const now = new Date();
  const in1h = new Date(now.getTime() + 60 * 60 * 1000);
  const toLocal = (d) => d.toISOString().slice(0, 16);
  const [f, setF] = useState(initial || {
    name: "", recurring: false,
    start: toLocal(now), end: toLocal(in1h),
    day_of_week: 0, hour_start: 8, hour_end: 12, enabled: true, tratti_ids: [],
  });
  const [tratti, setTratti] = useState([]);
  React.useEffect(() => {
    api.get("/v1/district").then((r) => setTratti(r.data.tratti || [])).catch(() => {});
  }, []);
  const setV = (k, v) => setF({ ...f, [k]: v });
  const toggleTratto = (id) => {
    const arr = f.tratti_ids || [];
    setF({ ...f, tratti_ids: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] });
  };
  const submit = (e) => {
    e.preventDefault();
    const payload = { ...f };
    if (f.recurring) {
      payload.day_of_week = Number(f.day_of_week);
      payload.hour_start = Number(f.hour_start);
      payload.hour_end = Number(f.hour_end);
    } else {
      payload.start = new Date(f.start).toISOString();
      payload.end = new Date(f.end).toISOString();
    }
    onSubmit(payload);
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-400">Nome finestra</Label>
        <Input required value={f.name} onChange={(e) => setV("name", e.target.value)} placeholder="Es. Manutenzione pompe" className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" data-testid="mnt-name-input" />
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={f.recurring} onCheckedChange={(v) => setV("recurring", v)} data-testid="mnt-recurring-switch" />
        <Label className="text-xs text-slate-300">Ricorrente settimanale</Label>
      </div>
      {f.recurring ? (
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Giorno</Label>
            <Select value={String(f.day_of_week)} onValueChange={(v) => setV("day_of_week", v)}>
              <SelectTrigger className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono text-xs" data-testid="mnt-dow-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-slate-800">
                {DAYS.map((d) => <SelectItem key={d.v} value={String(d.v)} className="font-mono text-xs">{d.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Ora inizio</Label>
            <Input type="number" min="0" max="23" value={f.hour_start} onChange={(e) => setV("hour_start", e.target.value)} className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Ora fine</Label>
            <Input type="number" min="0" max="24" value={f.hour_end} onChange={(e) => setV("hour_end", e.target.value)} className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Inizio</Label>
            <Input type="datetime-local" required value={f.start} onChange={(e) => setV("start", e.target.value)} className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" data-testid="mnt-start-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Fine</Label>
            <Input type="datetime-local" required value={f.end} onChange={(e) => setV("end", e.target.value)} className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" data-testid="mnt-end-input" />
          </div>
        </div>
      )}
      <div className="flex items-center gap-3">
        <Switch checked={f.enabled} onCheckedChange={(v) => setV("enabled", v)} />
        <Label className="text-xs text-slate-300">Attiva</Label>
      </div>
      <div className="border border-slate-800 rounded-md p-3">
        <Label className="text-xs uppercase tracking-wider text-slate-400 mb-2 block">
          Tratti coinvolti ({(f.tratti_ids || []).length}) · vuoto = tutto il distretto
        </Label>
        {tratti.length === 0 ? (
          <div className="text-xs text-slate-500 font-mono">Nessun tratto disponibile</div>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="mnt-tratti-selector">
            {tratti.map((t) => {
              const active = (f.tratti_ids || []).includes(t.id);
              return (
                <button type="button" key={t.id} onClick={() => toggleTratto(t.id)}
                  data-testid={`mnt-tratto-${t.id}`}
                  className={`px-2.5 py-1 rounded text-xs font-mono border ${
                    active ? "border-[#F59E0B] bg-[#F59E0B]/15 text-[#F59E0B]" : "border-slate-700 text-slate-400 hover:text-slate-200"
                  }`}>
                  {active && "● "}{t.id}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" type="button" onClick={onCancel} className="border-slate-700">Annulla</Button>
        <Button type="submit" className="bg-[#3EB6F0] text-[#070B14] hover:bg-[#2CA4DE] gap-2" data-testid="mnt-save-button">
          <FloppyDisk weight="bold" size={14} /> Salva
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function MaintenancePanel() {
  const [windows, setWindows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState({ type: null, item: null });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/v1/maintenance");
      setWindows(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Errore caricamento")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    try {
      if (edit.item) await api.put(`/v1/admin/maintenance/${edit.item.id}`, form);
      else await api.post("/v1/admin/maintenance", form);
      toast.success("Finestra salvata");
      setEdit({ type: null, item: null }); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Errore")); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare la finestra?")) return;
    try { await api.delete(`/v1/admin/maintenance/${id}`); toast.success("Eliminata"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Errore")); }
  };

  if (loading) return <div className="text-center py-8 text-xs text-slate-500 font-mono">Caricamento...</div>;

  return (
    <div className="bg-[#111827] border border-slate-800 rounded-lg overflow-hidden" data-testid="maintenance-panel">
      <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wrench size={16} weight="duotone" className="text-[#F59E0B]" />
          <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Manutenzione programmata · disattiva TUTTE le regole quando attiva</div>
        </div>
        <Dialog open={edit.type === "new"} onOpenChange={(o) => setEdit(o ? { type: "new", item: null } : { type: null, item: null })}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-[#3EB6F0] text-[#070B14] hover:bg-[#2CA4DE] gap-2" data-testid="add-maintenance-button">
              <Plus weight="bold" size={14} /> Nuova finestra
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#111827] border-slate-800 text-slate-100">
            <DialogHeader><DialogTitle className="font-heading">Nuova finestra manutenzione</DialogTitle></DialogHeader>
            <MaintenanceForm onSubmit={save} onCancel={() => setEdit({ type: null, item: null })} />
          </DialogContent>
        </Dialog>
      </div>
      <table className="w-full text-xs">
        <thead className="text-slate-500 uppercase tracking-widest">
          <tr className="border-b border-slate-800">
            <th className="text-left px-4 py-2 font-mono">Nome</th>
            <th className="text-left px-2 py-2 font-mono">Tipo</th>
            <th className="text-left px-2 py-2 font-mono">Quando</th>
            <th className="text-center px-2 py-2 font-mono">Attiva</th>
            <th className="text-center px-3 py-2 font-mono">Azioni</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {windows.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-500">Nessuna finestra configurata</td></tr>}
          {windows.map((w) => (
            <tr key={w.id} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`mnt-row-${w.id}`}>
              <td className="px-4 py-2 text-slate-200 font-semibold">{w.name}</td>
              <td className="px-2 py-2 text-[#3EB6F0]">{w.recurring ? "Ricorrente" : "Puntuale"}</td>
              <td className="px-2 py-2 text-slate-400">
                {w.recurring
                  ? `${DAYS[w.day_of_week]?.l} ${w.hour_start}:00–${w.hour_end}:00`
                  : `${new Date(w.start).toLocaleString("it-IT")} → ${new Date(w.end).toLocaleString("it-IT")}`}
              </td>
              <td className="px-2 py-2 text-center">
                {w.enabled ? <span className="text-[#10B981]">●</span> : <span className="text-slate-600">○</span>}
              </td>
              <td className="px-3 py-2">
                <div className="flex items-center justify-center gap-3">
                  <Dialog open={edit.type === "edit" && edit.item?.id === w.id} onOpenChange={(o) => setEdit(o ? { type: "edit", item: w } : { type: null, item: null })}>
                    <DialogTrigger asChild>
                      <button className="text-slate-500 hover:text-[#3EB6F0]" data-testid={`edit-mnt-${w.id}`}><PencilSimple size={14} weight="bold" /></button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#111827] border-slate-800 text-slate-100">
                      <DialogHeader><DialogTitle className="font-heading">Modifica finestra</DialogTitle></DialogHeader>
                      <MaintenanceForm initial={{ ...w, start: w.start?.slice(0,16), end: w.end?.slice(0,16) }} onSubmit={save} onCancel={() => setEdit({ type: null, item: null })} />
                    </DialogContent>
                  </Dialog>
                  <button className="text-slate-500 hover:text-[#EF4444]" onClick={() => del(w.id)} data-testid={`delete-mnt-${w.id}`}><Trash size={14} weight="bold" /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
